/**
 * PromTrend — line graph of score progression for a single survey.
 * Iter 68. Uses recharts (already in package.json from earlier iter).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { TrendingDown, TrendingUp, ArrowLeft, Activity } from 'lucide-react';

const NICE_NAMES = {
  odi: 'Oswestry Disability Index',
  ndi: 'Neck Disability Index',
  quickdash: 'QuickDASH (upper limb)',
  vas: 'Pain VAS',
};

export default function PromTrend() {
  const { user } = useAuth();
  const { patient_id, survey_key } = useParams();
  const isStaff = user?.role === 'admin' || user?.role === 'clinician';
  const targetPid = isStaff ? patient_id : user?.user_id;

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!targetPid || !survey_key) return;
    api.get(`/proms/responses/trend/${targetPid}`, { params: { survey_key } })
       .then(r => setRows(r.data || []))
       .catch(() => {});
  }, [targetPid, survey_key]);

  const data = rows.map(r => ({
    date: (r.submitted_at || '').slice(0, 10),
    score: r.score_pct,
    label: r.label,
  }));

  const first = data[0]?.score;
  const last = data[data.length - 1]?.score;
  const delta = (last != null && first != null) ? Math.round(last - first) : null;
  const goodDirection = delta != null && delta < 0;  // lower scores = better

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <Link to={isStaff ? `/admin/patients/${patient_id}/proms` : '/patient/proms'}
            className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-4">
        <ArrowLeft className="w-3 h-3"/> Back
      </Link>
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">TREND</div>
      <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2" data-testid="prom-trend-title">
        {NICE_NAMES[survey_key] || survey_key}
      </h1>
      <p className="text-fg-muted text-sm mb-6">
        {data.length} response{data.length === 1 ? '' : 's'} over time.
        {data.length > 1 && delta != null && (
          <span className={`inline-flex items-center gap-1 ml-2 font-semibold ${goodDirection ? 'text-[#10A862]' : delta === 0 ? 'text-fg-muted' : 'text-[#EF4444]'}`}>
            {goodDirection ? <TrendingDown className="w-3.5 h-3.5"/> : <TrendingUp className="w-3.5 h-3.5"/>}
            {delta > 0 ? '+' : ''}{delta} pts since first measure
          </span>
        )}
      </p>

      {data.length === 0 ? (
        <div className="bento-card text-center py-10 text-sm text-fg-muted" data-testid="prom-trend-empty">
          No responses yet. Take the survey to start tracking.
        </div>
      ) : (
        <>
          <div className="bento-card h-[320px]" data-testid="prom-trend-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <LineChart data={data} margin={{ top: 20, right: 20, bottom: 10, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8DFC8"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }}/>
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} label={{ value: 'Score %', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}/>
                <Tooltip formatter={(v, name, props) => [`${v}% (${props.payload.label})`, 'Score']}/>
                <ReferenceLine y={20} stroke="#10A862" strokeDasharray="4 4" label={{ value: 'Mild', fontSize: 10, fill: '#10A862', position: 'right' }}/>
                <ReferenceLine y={40} stroke="#FFB000" strokeDasharray="4 4" label={{ value: 'Moderate', fontSize: 10, fill: '#FFB000', position: 'right' }}/>
                <ReferenceLine y={60} stroke="#EF4444" strokeDasharray="4 4" label={{ value: 'Severe', fontSize: 10, fill: '#EF4444', position: 'right' }}/>
                <Line type="monotone" dataKey="score" stroke="#003D80" strokeWidth={2.5} dot={{ r: 5, fill: '#FFB000', stroke: '#003D80', strokeWidth: 2 }} activeDot={{ r: 7 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 bento-card">
            <h3 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
              <Activity className="w-4 h-4 text-brand"/> History
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-muted text-xs uppercase tracking-wider border-b border-soft">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Score</th>
                  <th className="text-left py-2">Severity</th>
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((d, i) => (
                  <tr key={i} className="border-b border-soft/50">
                    <td className="py-2">{d.date}</td>
                    <td className="py-2 font-mono font-semibold">{d.score}%</td>
                    <td className="py-2">{d.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
